import React, { useState } from 'react';
import { ArrowLeft, CheckCircle, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';

const SECTOR_OPTIONS = [
  { value: 'early_childhood', label: 'Early Childhood Education / Child Care' },
  { value: 'k12', label: 'K-12 Education' },
  { value: 'policy', label: 'Public Policy / Government' },
  { value: 'nonprofit', label: 'Nonprofit / Community Organizations' },
  { value: 'philanthropy', label: 'Philanthropy / Fundraising' },
  { value: 'research', label: 'Research / Academia' },
  { value: 'healthcare', label: 'Healthcare / Child Development' },
  { value: 'legal', label: 'Legal / Advocacy / Law' },
  { value: 'business', label: 'Business / Finance' },
  { value: 'communications', label: 'Communications / Media' },
  { value: 'tech', label: 'Technology / Data' },
  { value: 'other', label: 'Other' }
];

const SKILL_OPTIONS = [
  { value: 'governance', label: 'Nonprofit Governance / Leadership' },
  { value: 'finance', label: 'Financial Oversight / Budgeting' },
  { value: 'fundraising', label: 'Fundraising / Development' },
  { value: 'advocacy_policy', label: 'Legislative & Policy Advocacy' },
  { value: 'community', label: 'Community Engagement / Outreach' },
  { value: 'strategic', label: 'Strategic Planning' },
  { value: 'hr', label: 'Human Resources / Workforce Development' },
  { value: 'marketing', label: 'Marketing / Communications' },
  { value: 'evaluation', label: 'Program Evaluation / Research' },
  { value: 'legal_skill', label: 'Legal / Compliance' }
];

const FOCUS_OPTIONS = [
  { value: 'quality', label: 'Improving Program Quality' },
  { value: 'access', label: 'Equitable Access & Affordability' },
  { value: 'workforce', label: 'ECE Workforce & Compensation' },
  { value: 'birth3', label: 'Birth-to-Three Programs' },
  { value: 'prek', label: 'Pre-K & School Readiness' },
  { value: 'families', label: 'Family & Community Support' },
  { value: 'equity', label: 'Racial & Economic Equity' },
  { value: 'funding', label: 'Public Funding & Budget Advocacy' },
  { value: 'research_data', label: 'Research, Data & Policy' }
];

const COMMITTEE_OPTIONS = [
  { value: 'exec', label: 'Executive Committee' },
  { value: 'finance_audit', label: 'Finance & Audit' },
  { value: 'governance_nom', label: 'Governance & Nominations' },
  { value: 'advocacy_policy', label: 'Advocacy & Policy' },
  { value: 'development', label: 'Development & Fundraising' },
  { value: 'prog_eval', label: 'Program & Evaluation' },
  { value: 'community_outreach', label: 'Community Engagement' },
  { value: 'not_sure', label: 'Not sure yet' }
];

const CheckboxGroup = ({ options, selected, onChange, columns = 2 }) => (
  <div className={`grid gap-2.5 ${columns === 3 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2'}`}>
    {options.map(opt => (
      <label
        key={opt.value}
        className={`flex items-start gap-2.5 px-3 py-2.5 rounded-lg border cursor-pointer transition-all text-sm ${
          selected.includes(opt.value)
            ? 'border-[#2e7d5e] bg-[#e8f4ee]'
            : 'border-slate-200 bg-white hover:border-[#3d9e78] hover:bg-[#e8f4ee]/50'
        }`}
      >
        <input
          type="checkbox"
          checked={selected.includes(opt.value)}
          onChange={() => {
            onChange(selected.includes(opt.value)
              ? selected.filter(v => v !== opt.value)
              : [...selected, opt.value]);
          }}
          className="mt-0.5 accent-[#2e7d5e]"
        />
        <span className="text-slate-700 leading-snug">{opt.label}</span>
      </label>
    ))}
  </div>
);

const BoardInterestForm = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    title_role: '', organization: '', ward: '', pronouns: '',
    sectors: [], skills: [], years_ece: '', professional_bio: '',
    focus_areas: [], why_board: '', contribution: '',
    hours_per_month: '', committees: [], fundraising_comfort: '',
    board_history: '', start_date: '', term_length: '',
    networks: '', referral: '', referral_name: '',
    additional_info: '', resume_link: '', preferred_contact: ''
  });
  const [consent, setConsent] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.title_role || !formData.organization || !formData.professional_bio || !formData.why_board || !formData.contribution) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!consent) {
      toast.error('Please agree to the consent statement');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/board-interest/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        setIsSubmitted(true);
        toast.success('Interest form submitted successfully!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Submission failed. Please try again.');
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <>
        <Header />
        <div id="main-content" role="main" tabIndex={-1} className="min-h-screen bg-[#faf8f3] flex items-center justify-center pt-20 px-4">
          <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl p-10 text-center" data-testid="board-interest-success">
            <div className="w-20 h-20 bg-[#e8f4ee] rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-[#2e7d5e]" />
            </div>
            <h2 className="text-2xl font-bold text-[#1a2e3d] mb-3">Thank You for Your Interest!</h2>
            <p className="text-slate-600 mb-6">
              We've received your Board Interest Form. A member of DCELC's board recruitment committee will be in touch within 2-3 weeks. We look forward to learning more about you.
            </p>
            <Button
              onClick={() => navigate('/board-of-directors')}
              className="bg-[#2e7d5e] hover:bg-[#3d9e78] text-white px-8"
              data-testid="board-interest-back-btn"
            >
              Back to Board of Directors
            </Button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const SectionHeading = ({ num, title }) => (
    <div className="flex items-center gap-3 mb-5 mt-10 pb-2.5 border-b-2 border-[#e8f4ee] first:mt-0">
      <div className="w-8 h-8 rounded-full bg-[#2e7d5e] text-white text-sm font-bold flex items-center justify-center shrink-0">{num}</div>
      <h2 className="text-lg font-semibold text-[#2e7d5e]" style={{ fontFamily: "'Playfair Display', serif" }}>{title}</h2>
    </div>
  );

  const Callout = ({ children }) => (
    <div className="bg-[#e8f4ee] border-l-4 border-[#2e7d5e] rounded-r-xl px-5 py-4 mb-5">
      <p className="text-sm text-[#1a2e3d] leading-relaxed">{children}</p>
    </div>
  );

  return (
    <>
      <Header />
      <div id="main-content" role="main" tabIndex={-1} className="min-h-screen bg-[#faf8f3]">
        {/* Header */}
        <div className="relative bg-[#2e7d5e] overflow-hidden">
          <div className="absolute top-0 right-0 w-80 h-full bg-gradient-to-bl from-[#c9972c]/15 to-transparent pointer-events-none" />
          <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-14">
            <button
              onClick={() => navigate('/board-of-directors')}
              className="flex items-center gap-2 text-white/70 hover:text-white mb-5 transition-colors text-sm"
              data-testid="board-interest-back-link"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Board of Directors
            </button>
            <p className="text-[#f0c96a] text-xs font-semibold uppercase tracking-[0.18em] mb-3">DC Early Learning Collaborative</p>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Board of Directors<br /><span className="text-[#f0c96a]">Interest & Intake Form</span>
            </h1>
            <p className="text-white/85 text-base leading-relaxed max-w-xl">
              Thank you for your interest in serving on the DCELC Board of Directors. Please complete the form below so we can learn more about you and explore how your expertise can help advance equitable early childhood education across Washington, DC.
            </p>
          </div>
        </div>

        {/* Mission Strip */}
        <div className="bg-[#1a2e3d] px-4 sm:px-6 py-4">
          <div className="max-w-3xl mx-auto">
            <p className="text-sm text-white/80 italic leading-relaxed">
              <strong className="text-[#f0c96a] not-italic">Our Mission:</strong> To work with a wide range of stakeholders to prepare children from birth to school entry for success in school, careers, and life — by improving the quality, quantity, and affordability of early care and education services in Washington, DC.
            </p>
          </div>
        </div>

        {/* Form */}
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 pb-20">
          <form onSubmit={handleSubmit} data-testid="board-interest-form">

            {/* Section 1 */}
            <SectionHeading num="1" title="Personal Information" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="first_name" className="block text-xs font-semibold text-[#1a2e3d] mb-1">First Name <span className="text-[#c9972c]">*</span></label>
                <Input id="first_name" name="first_name" value={formData.first_name} onChange={handleChange} required placeholder="First name" data-testid="bi-first-name" />
              </div>
              <div>
                <label htmlFor="last_name" className="block text-xs font-semibold text-[#1a2e3d] mb-1">Last Name <span className="text-[#c9972c]">*</span></label>
                <Input id="last_name" name="last_name" value={formData.last_name} onChange={handleChange} required placeholder="Last name" data-testid="bi-last-name" />
              </div>
              <div>
                <label htmlFor="email" className="block text-xs font-semibold text-[#1a2e3d] mb-1">Email Address <span className="text-[#c9972c]">*</span></label>
                <Input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required placeholder="you@example.com" data-testid="bi-email" />
              </div>
              <div>
                <label htmlFor="phone" className="block text-xs font-semibold text-[#1a2e3d] mb-1">Phone Number</label>
                <Input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} placeholder="(202) 000-0000" data-testid="bi-phone" />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="title_role" className="block text-xs font-semibold text-[#1a2e3d] mb-1">Current Title / Role <span className="text-[#c9972c]">*</span></label>
                <Input id="title_role" name="title_role" value={formData.title_role} onChange={handleChange} required placeholder="e.g., Executive Director, Senior Policy Analyst" data-testid="bi-title" />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="organization" className="block text-xs font-semibold text-[#1a2e3d] mb-1">Organization / Employer <span className="text-[#c9972c]">*</span></label>
                <Input id="organization" name="organization" value={formData.organization} onChange={handleChange} required placeholder="Organization name" data-testid="bi-organization" />
              </div>
              <div>
                <label htmlFor="ward" className="block text-xs font-semibold text-[#1a2e3d] mb-1">DC Ward (if applicable)</label>
                <select id="ward" name="ward" value={formData.ward} onChange={handleChange} className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2e7d5e]" data-testid="bi-ward">
                  <option value="">Select ward...</option>
                  {['Ward 1','Ward 2','Ward 3','Ward 4','Ward 5','Ward 6','Ward 7','Ward 8','Outside DC'].map(w => <option key={w}>{w}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="pronouns" className="block text-xs font-semibold text-[#1a2e3d] mb-1">Preferred Pronouns</label>
                <Input id="pronouns" name="pronouns" value={formData.pronouns} onChange={handleChange} placeholder="e.g., she/her, he/him, they/them" data-testid="bi-pronouns" />
              </div>
            </div>

            <hr className="my-8 border-slate-200" />

            {/* Section 2 */}
            <SectionHeading num="2" title="Professional Background & Expertise" />
            <Callout>
              DCELC's board includes educators, policymakers, advocates, researchers, and community leaders. We are especially seeking expertise that helps advance <strong>equitable access to high-quality early childhood education</strong> across all eight wards of DC.
            </Callout>
            <div className="space-y-5">
              <div>
                <label className="block text-xs font-semibold text-[#1a2e3d] mb-1">Which sectors best describe your professional background? <span className="text-[#c9972c]">*</span></label>
                <p className="text-xs text-slate-500 mb-2">Select all that apply.</p>
                <CheckboxGroup options={SECTOR_OPTIONS} selected={formData.sectors} onChange={(v) => setFormData(prev => ({...prev, sectors: v}))} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#1a2e3d] mb-1">Which board skills or functions could you contribute? <span className="text-[#c9972c]">*</span></label>
                <p className="text-xs text-slate-500 mb-2">Select all that apply.</p>
                <CheckboxGroup options={SKILL_OPTIONS} selected={formData.skills} onChange={(v) => setFormData(prev => ({...prev, skills: v}))} />
              </div>
              <div>
                <label htmlFor="years_ece" className="block text-xs font-semibold text-[#1a2e3d] mb-1">Years of experience in or adjacent to early childhood education</label>
                <select id="years_ece" name="years_ece" value={formData.years_ece} onChange={handleChange} className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2e7d5e]" data-testid="bi-years-ece">
                  <option value="">Select...</option>
                  {['Less than 2 years','2-5 years','6-10 years','11-20 years','20+ years'].map(y => <option key={y}>{y}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="professional_bio" className="block text-xs font-semibold text-[#1a2e3d] mb-1">Brief Professional Biography <span className="text-[#c9972c]">*</span></label>
                <p className="text-xs text-slate-500 mb-1">Please summarize your background, key roles, and accomplishments (250 words or fewer).</p>
                <textarea id="professional_bio" name="professional_bio" value={formData.professional_bio} onChange={handleChange} required rows={5} className="flex w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2e7d5e]" placeholder="Describe your professional background..." data-testid="bi-bio" />
              </div>
            </div>

            <hr className="my-8 border-slate-200" />

            {/* Section 3 */}
            <SectionHeading num="3" title="Connection to DCELC's Mission" />
            <div className="space-y-5">
              <div>
                <label className="block text-xs font-semibold text-[#1a2e3d] mb-1">Which of DCELC's focus areas are most aligned with your experience or passion? <span className="text-[#c9972c]">*</span></label>
                <p className="text-xs text-slate-500 mb-2">Select all that apply.</p>
                <CheckboxGroup options={FOCUS_OPTIONS} selected={formData.focus_areas} onChange={(v) => setFormData(prev => ({...prev, focus_areas: v}))} columns={3} />
              </div>
              <div>
                <label htmlFor="why_board" className="block text-xs font-semibold text-[#1a2e3d] mb-1">Why do you want to serve on the DCELC Board of Directors? <span className="text-[#c9972c]">*</span></label>
                <p className="text-xs text-slate-500 mb-1">What draws you to DCELC's mission and what do you hope to contribute?</p>
                <textarea id="why_board" name="why_board" value={formData.why_board} onChange={handleChange} required rows={5} className="flex w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2e7d5e]" placeholder="Share your motivation..." data-testid="bi-why" />
              </div>
              <div>
                <label htmlFor="contribution" className="block text-xs font-semibold text-[#1a2e3d] mb-1">What unique perspective, network, or resources would you bring? <span className="text-[#c9972c]">*</span></label>
                <p className="text-xs text-slate-500 mb-1">Consider your community connections, institutional relationships, or lived experience.</p>
                <textarea id="contribution" name="contribution" value={formData.contribution} onChange={handleChange} required rows={4} className="flex w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2e7d5e]" placeholder="Describe your unique contributions..." data-testid="bi-contribution" />
              </div>
            </div>

            <hr className="my-8 border-slate-200" />

            {/* Section 4 */}
            <SectionHeading num="4" title="Board Commitment & Availability" />
            <Callout>
              DCELC Board members are expected to attend quarterly board meetings, participate in at least one committee, support fundraising efforts, and serve as ambassadors for the organization throughout Washington, DC.
            </Callout>
            <div className="space-y-5">
              <div>
                <label htmlFor="hours_per_month" className="block text-xs font-semibold text-[#1a2e3d] mb-1">How many hours per month can you commit?</label>
                <select id="hours_per_month" name="hours_per_month" value={formData.hours_per_month} onChange={handleChange} className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2e7d5e]" data-testid="bi-hours">
                  <option value="">Select...</option>
                  {['1-3 hours/month','4-6 hours/month','7-10 hours/month','10+ hours/month'].map(h => <option key={h}>{h}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#1a2e3d] mb-1">Which committee(s) would you be interested in?</label>
                <p className="text-xs text-slate-500 mb-2">Select all that apply.</p>
                <CheckboxGroup options={COMMITTEE_OPTIONS} selected={formData.committees} onChange={(v) => setFormData(prev => ({...prev, committees: v}))} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#1a2e3d] mb-2">How comfortable are you with fundraising efforts?</label>
                <div className="flex gap-2">
                  {['1','2','3','4','5'].map(n => (
                    <label key={n} className={`w-11 h-11 rounded-lg border flex items-center justify-center text-sm font-semibold cursor-pointer transition-all ${
                      formData.fundraising_comfort === n
                        ? 'bg-[#2e7d5e] border-[#2e7d5e] text-white'
                        : 'border-slate-200 text-slate-500 hover:border-[#2e7d5e] hover:text-[#2e7d5e]'
                    }`}>
                      <input type="radio" name="fundraising_comfort" value={n} checked={formData.fundraising_comfort === n} onChange={handleChange} className="sr-only" />
                      {n}
                    </label>
                  ))}
                </div>
                <div className="flex justify-between text-xs text-slate-400 mt-1 max-w-[15rem]">
                  <span>Not comfortable</span><span>Very comfortable</span>
                </div>
              </div>
              <div>
                <label htmlFor="board_history" className="block text-xs font-semibold text-[#1a2e3d] mb-1">Previous nonprofit board experience?</label>
                <textarea id="board_history" name="board_history" value={formData.board_history} onChange={handleChange} rows={3} className="flex w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2e7d5e]" placeholder="List organizations and approximate years of service, or type N/A" data-testid="bi-history" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="start_date" className="block text-xs font-semibold text-[#1a2e3d] mb-1">Earliest available start date</label>
                  <Input id="start_date" name="start_date" value={formData.start_date} onChange={handleChange} placeholder="e.g., January 2026, Immediately" data-testid="bi-start" />
                </div>
                <div>
                  <label htmlFor="term_length" className="block text-xs font-semibold text-[#1a2e3d] mb-1">Preferred term length</label>
                  <select id="term_length" name="term_length" value={formData.term_length} onChange={handleChange} className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2e7d5e]" data-testid="bi-term">
                    <option value="">Select...</option>
                    {['1 year (exploratory)','2 years','3 years','Open to board recommendation'].map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <hr className="my-8 border-slate-200" />

            {/* Section 5 */}
            <SectionHeading num="5" title="Networks & Community Connections" />
            <div className="space-y-4">
              <div>
                <label htmlFor="networks" className="block text-xs font-semibold text-[#1a2e3d] mb-1">Which professional networks or communities are you connected to in DC?</label>
                <p className="text-xs text-slate-500 mb-1">e.g., Under 3 DC, DCAEYC, Head Start associations, DC Council, philanthropy networks</p>
                <textarea id="networks" name="networks" value={formData.networks} onChange={handleChange} rows={3} className="flex w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2e7d5e]" placeholder="List your key networks and affiliations..." data-testid="bi-networks" />
              </div>
              <div>
                <label htmlFor="referral" className="block text-xs font-semibold text-[#1a2e3d] mb-1">How did you hear about this board opportunity?</label>
                <select id="referral" name="referral" value={formData.referral} onChange={handleChange} className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2e7d5e]" data-testid="bi-referral">
                  <option value="">Select...</option>
                  {['DCELC website','Current DCELC board member or staff','Colleague or peer referral','Social media (LinkedIn, X/Twitter, Facebook)','Newsletter or email','Community event or meeting','Other'].map(r => <option key={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="referral_name" className="block text-xs font-semibold text-[#1a2e3d] mb-1">If referred by someone, please share their name (optional)</label>
                <Input id="referral_name" name="referral_name" value={formData.referral_name} onChange={handleChange} placeholder="Name of referral" data-testid="bi-referral-name" />
              </div>
            </div>

            <hr className="my-8 border-slate-200" />

            {/* Section 6 */}
            <SectionHeading num="6" title="Additional Information" />
            <div className="space-y-4">
              <div>
                <label htmlFor="additional_info" className="block text-xs font-semibold text-[#1a2e3d] mb-1">Anything else you'd like the recruitment committee to know?</label>
                <textarea id="additional_info" name="additional_info" value={formData.additional_info} onChange={handleChange} rows={4} className="flex w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2e7d5e]" placeholder="Additional context, questions, or information..." data-testid="bi-additional" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="resume_link" className="block text-xs font-semibold text-[#1a2e3d] mb-1">LinkedIn profile or online bio (optional)</label>
                  <Input id="resume_link" name="resume_link" value={formData.resume_link} onChange={handleChange} placeholder="https://linkedin.com/in/..." data-testid="bi-linkedin" />
                </div>
                <div>
                  <label htmlFor="preferred_contact" className="block text-xs font-semibold text-[#1a2e3d] mb-1">Preferred contact method</label>
                  <select id="preferred_contact" name="preferred_contact" value={formData.preferred_contact} onChange={handleChange} className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2e7d5e]" data-testid="bi-contact-method">
                    <option value="">Select...</option>
                    {['Email','Phone call','Video call (Zoom/Teams)'].map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Consent & Submit */}
            <div className="mt-10 space-y-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 accent-[#2e7d5e]" data-testid="bi-consent" />
                <span className="text-sm text-slate-500 leading-relaxed">
                  I consent to DCELC storing and using the information provided in this form for board recruitment purposes. I understand my information will not be shared outside of the organization.
                </span>
              </label>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-[#2e7d5e] hover:bg-[#3d9e78] text-white px-10 py-6 text-base font-semibold shadow-lg"
                data-testid="bi-submit-btn"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" />Submitting...</span>
                ) : 'Submit Interest Form'}
              </Button>
            </div>
          </form>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default BoardInterestForm;
